/**
 * Which computer an OPVisual item should aim at when the operator has not
 * said, or `null` to leave it unset and ask.
 *
 * The routing key is the MACHINE (see `sendOpVisual`): a send to the wrong
 * bridge is accepted and silently lost, so this may only answer when the
 * answer is unambiguous. Tiers, first match wins:
 *
 *  1. **Another OPVisual item in this project is already aimed** at a bridge
 *     that still resolves -> the same one. There is one OPVisual; a second
 *     item disagreeing with the first is never what anyone meant, and this
 *     is what makes "drop a Trigger next to the Grid" need no setup at all.
 *  2. **Exactly one bridge is paired** -> that one.
 *  3. **Exactly one paired bridge reports a Windows host** -> that one.
 *     OPVisual is Windows-only (Spout), and the operator's rig is one
 *     Windows streaming PC plus the Ableton Mac (2026-09-24). `platform`
 *     comes off each bridge's live `/health`, so an offline machine simply
 *     does not count rather than being guessed about.
 *  4. **Exactly one bridge has a reachable OBS** -> that one. OPVisual runs
 *     beside OBS on this rig (operator, 2026-09-24).
 *
 * Anything else returns `null`, the Inspector shows the machine picker, and
 * the item refuses to send until a machine is chosen.
 *
 * Returns the id to STORE: the node id when the row has one, because items
 * ride the synced project document and a local row id means nothing on the
 * other machine (same rule as `SingleMachineField`).
 */

import { bridges, getConnection, resolveBridge } from '../opbridge/store.ts';
import { activeProject } from '../layout/projectStore.ts';
import { obsCapableBridges } from '../obs/scenes.ts';
import { DEFAULT_OPV_VIA, type OpvVia } from './wire.ts';

export function autoPickOpVisualBridge(excludeItemId?: string): string | null {
  const rows = bridges();
  if (rows.length === 0) return null;
  const store = (rowId: string): string => rows.find((b) => b.id === rowId)?.nodeId ?? rowId;

  const project = activeProject();
  if (project) {
    for (const page of project.pages) {
      for (const it of page.items) {
        if (it.id === excludeItemId) continue;
        if (it.kind !== 'opvisualTrigger' && it.kind !== 'opvisualGrid') continue;
        const first = it.bridgeIds?.[0];
        if (!first) continue;
        const row = resolveBridge(first);
        if (row) return store(row.id);
      }
    }
  }

  if (rows.length === 1) return store(rows[0]!.id);

  const windows = rows.filter((b) => getConnection(b.id)?.health()?.platform === 'windows');
  if (windows.length === 1) return store(windows[0]!.id);

  const obs = obsCapableBridges(rows.map((b) => b.id));
  if (obs.length === 1) return store(obs[0]!);

  return null;
}

/**
 * Every machine the main PANIC must also panic OPVisual on: the "Sends to" of
 * each OPVisual item in this project, one entry per machine and channel.
 *
 * Aimed items only, never "every bridge". Sending OPVisual's `panic` to a
 * bridge no item points at would reach an OPVisual the operator never wired to
 * OPButtons (on the dev PC that is the live one, so every panic smoke would
 * empty its program), and a project with no OPVisual items has nothing for a
 * panic to report about OPVisual. OPVisual asked for either shape
 * (`OPApps/opbuttons-opvisual.md`, 2026-10-05).
 *
 * An aimed id whose row is gone is kept as stored, so the panic toast names it
 * as a miss rather than leaving it out silently.
 */
export function opVisualPanicAims(): Array<{ bridgeId: string; via: OpvVia }> {
  const project = activeProject();
  if (!project) return [];
  const seen = new Set<string>();
  const out: Array<{ bridgeId: string; via: OpvVia }> = [];
  for (const page of project.pages) {
    for (const it of page.items) {
      if (it.kind !== 'opvisualTrigger' && it.kind !== 'opvisualGrid') continue;
      const first = it.bridgeIds?.[0];
      if (!first) continue;
      const via = it.sendVia ?? DEFAULT_OPV_VIA;
      const key = `${resolveBridge(first)?.id ?? first}|${via}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ bridgeId: first, via });
    }
  }
  return out;
}
